/**
 * Purpose: Compact, responsive toolbar used across society section pages (Businesses,
 *          Posts, Events, Property, Ads, Places, Institutes, Mosques, Free Items).
 *
 * Inputs:
 *   - title (string): Section title (e.g. "Businesses").
 *   - count (number): Total item count for the section.
 *   - itemLabel ({ singular, plural }): Words used in "{count} {singular|plural}".
 *   - societyName (string): Society name, shown on desktop count line only.
 *   - searchPlaceholder (string): Placeholder for the search input.
 *   - searchValue (string): Controlled search value.
 *   - onSearchChange (fn<string>): Called with new search text.
 *   - addButton ({ label, onClick } | null): Optional add action on the right.
 *   - activeFilterCount (number): Count of non-default filters for the badge (optional).
 *   - children (ReactNode): Extra filter controls (rendered alongside the search on
 *     desktop, collapsed behind a toggle on mobile).
 *
 * Outputs: Rendered toolbar JSX.
 *
 * Behavior:
 *   - Mobile (<sm): single compact row with title+count badge + icon add button;
 *     always-visible search; extra filters collapsed behind a "Filters" toggle so
 *     the content (list/grid) is visible above the fold.
 *   - Desktop (sm+): title + count and add button inline; search + extra filters
 *     shown together as a responsive grid.
 */
import React, { useState } from 'react';

const PlusIcon = (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const SearchIcon = (
  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35m1.35-5.65a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const SocietySectionToolbar = ({
  title,
  count = 0,
  itemLabel = { singular: 'item', plural: 'items' },
  societyName,
  searchPlaceholder = 'Search...',
  searchValue = '',
  onSearchChange,
  addButton = null,
  activeFilterCount = 0,
  children,
}) => {
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const hasExtraFilters = React.Children.count(children) > 0;
  const countLabel = `${count} ${count === 1 ? itemLabel.singular : itemLabel.plural}`;

  const handleSearch = (e) => {
    if (typeof onSearchChange === 'function') {
      onSearchChange(e.target.value);
    }
  };

  return (
    <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200">
      {/* Top row: title + count + optional add button */}
      <div className="px-3 py-2.5 sm:px-5 sm:py-4 flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <h2 className="text-base sm:text-2xl font-semibold sm:font-bold text-gray-900 truncate">
              {title}
            </h2>
            <span className="inline-flex items-center px-2 py-0.5 text-xs sm:text-sm font-medium rounded-full bg-gray-100 text-gray-700">
              {count.toLocaleString()}
            </span>
          </div>
          <p className="hidden sm:block text-gray-600 text-sm mt-1">
            {countLabel}
            {societyName ? ` in ${societyName}` : ''}
          </p>
        </div>
        {addButton && (
          <>
            <button
              type="button"
              onClick={addButton.onClick}
              className="sm:hidden inline-flex items-center justify-center gap-1 px-3 h-9 rounded-md bg-blue-600 text-white hover:bg-blue-700 flex-shrink-0 text-sm font-medium"
              aria-label={addButton.label}
            >
              {PlusIcon}
              <span>Add</span>
            </button>
            <button
              type="button"
              onClick={addButton.onClick}
              className="hidden sm:inline-flex items-center gap-1.5 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors text-sm font-medium flex-shrink-0"
            >
              {PlusIcon}
              <span>{addButton.label}</span>
            </button>
          </>
        )}
      </div>

      {/* Search + filters row */}
      <div className="px-3 pb-3 sm:px-5 sm:pb-5 space-y-3">
        {/* Mobile: search + filter toggle */}
        <div className="sm:hidden flex items-center gap-2">
          <label htmlFor={`section-search-${title}`} className="sr-only">
            Search {title.toLowerCase()}
          </label>
          <div className="relative flex-1">
            <span className="absolute inset-y-0 left-2.5 flex items-center pointer-events-none">
              {SearchIcon}
            </span>
            <input
              id={`section-search-${title}`}
              type="search"
              value={searchValue}
              onChange={handleSearch}
              placeholder={searchPlaceholder}
              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoComplete="off"
            />
          </div>
          {hasExtraFilters && (
            <button
              type="button"
              onClick={() => setMobileFiltersOpen((v) => !v)}
              aria-expanded={mobileFiltersOpen}
              aria-controls={`section-filters-${title}`}
              className={`inline-flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg border ${
                mobileFiltersOpen
                  ? 'border-blue-500 text-blue-700 bg-blue-50'
                  : 'border-gray-300 text-gray-700 bg-white'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 01.8 1.6L14 13.414V20a1 1 0 01-1.447.894l-4-2A1 1 0 018 18v-4.586L3.2 4.6A1 1 0 013 4z" />
              </svg>
              Filters
              {activeFilterCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-blue-600 text-white text-[10px] font-semibold">
                  {activeFilterCount}
                </span>
              )}
            </button>
          )}
        </div>

        {/* Mobile: collapsible extra filters */}
        {hasExtraFilters && (
          <div
            id={`section-filters-${title}`}
            className={`sm:hidden ${mobileFiltersOpen ? 'grid' : 'hidden'} grid-cols-1 gap-3 pt-1`}
          >
            {children}
          </div>
        )}

        {/* Desktop: search + extra filters on one responsive grid */}
        <div className={`hidden sm:grid gap-4 ${hasExtraFilters ? 'sm:grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-1'}`}>
          <div>
            <label
              htmlFor={`section-search-desktop-${title}`}
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Search
            </label>
            <input
              id={`section-search-desktop-${title}`}
              type="search"
              value={searchValue}
              onChange={handleSearch}
              placeholder={searchPlaceholder}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              autoComplete="off"
            />
          </div>
          {children}
        </div>
      </div>
    </div>
  );
};

export default SocietySectionToolbar;
